import React from 'react';
import type { AttendanceState } from '@graphql/generated-base';

/** What the funnel hands back while it runs: hold the end screen in place, and the cards to show. */
export type EmbeddedPostRsvpFunnelState = Readonly<{
  isHoldingPlace: boolean;
  successCards: React.ReactNode;
}>;

/** The end screen's reads, handed to the funnel; it renders the end screen back through `children`. */
export type EmbeddedPostRsvpFunnelProps = Readonly<{
  eventId: string;
  guestKey: string | null;
  hasCheckoutResult: boolean;
  attendanceResolved: boolean;
  attendanceState: AttendanceState | null;
  children: (state: EmbeddedPostRsvpFunnelState) => React.ReactElement | null;
  /** Shown by the loadable wrapper while the funnel's chunk loads; the funnel itself ignores it. */
  fallback?: React.ReactNode;
}>;

/** No funnel in the tree: the end screen holds nothing back and shows no cards. */
export const NO_EMBEDDED_POST_RSVP_FUNNEL: EmbeddedPostRsvpFunnelState = { isHoldingPlace: false, successCards: null };

/** The funnel's chunk is still arriving: hold the end screen in place rather than render nothing. */
export const EMBEDDED_POST_RSVP_FUNNEL_LOADING: EmbeddedPostRsvpFunnelState = { isHoldingPlace: true, successCards: null };

// Only the guest website injects a funnel. Everywhere else (website editor, admin preview, tests) the
// context is empty and the embedded end screen renders exactly as it did before the funnel existed.
const EmbeddedPostRsvpFunnelContext = React.createContext<React.ComponentType<EmbeddedPostRsvpFunnelProps> | null>(null);

export const EmbeddedPostRsvpFunnelProvider = EmbeddedPostRsvpFunnelContext.Provider;

export const useEmbeddedPostRsvpFunnelComponent = () => React.useContext(EmbeddedPostRsvpFunnelContext);

/**
 * Warms the injected funnel's chunk while the guest is still filling the form, so the end screen has it
 * in hand the moment they submit. No funnel injected, nothing to fetch.
 */
export const usePreloadEmbeddedPostRsvpFunnel = () => {
  const Funnel = useEmbeddedPostRsvpFunnelComponent();
  React.useEffect(() => {
    (Funnel as Readonly<{ preload?: () => void }> | null)?.preload?.();
  }, [Funnel]);
};
